import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ChakraFlex, FAIconName, useToastMessage } from '@equip.health/ui';

import {
  MaiChatEvents,
  MaiRatingLabel,
  MaiSendMeta,
} from '~/analytics/maiChatEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { MaiFeedbackRating } from '~/lib/types/mai/MaiFeedbackRating';
import { MaiFeedbackReason } from '~/lib/types/mai/MaiFeedbackReason';
import {
  useRetractMaiFeedbackMutation,
  useSubmitMaiFeedbackMutation,
} from '~/store/services/mai';

import { useAppSelector } from '~/store/hooks';
import {
  maiApi,
  maiHistoryArgs,
  useGetMaiMessagesQuery,
} from '~/store/services/mai';

import MaiChatComposer from './MaiChatComposer';
import MaiChatFeedbackModal from './MaiChatFeedbackModal';
import MaiChatHeader from './MaiChatHeader';
import MaiChatStream from './MaiChatStream';
import {
  MAI_FEEDBACK_ERROR_TOAST,
  MAI_FEEDBACK_RETRACTED_TOAST,
  MAI_FEEDBACK_SUCCESS_TOAST,
} from './maiFeedback.constants';
import { MAI_CHAT_ERROR_KIND_LABELS } from './maiChat.constants';
import { MaiChatErrorKind, MaiMessage } from './maiChat.types';
import { useMaiChatSession } from './MaiChatSessionContext';
import { useMaiSuggestedPrompts } from './useMaiSuggestedPrompts';

interface MaiChatPanelProps {
  patientFirstName: string;
  patientId: string;
  patientLastName: string;
}

const EMPTY_MESSAGES: MaiMessage[] = [];
Object.freeze(EMPTY_MESSAGES);

const RATING_LABELS: Record<MaiFeedbackRating, MaiRatingLabel> = {
  up: 'Up',
  down: 'Down',
};

const MaiChatPanel = ({
  patientFirstName,
  patientId,
  patientLastName,
}: MaiChatPanelProps) => {
  const { currentUser } = useGlobalContext();
  const analytics = useAnalytics();

  const {
    beginPatientSession,
    getCacheWriteVersion,
    getTurnState,
    patchMessages,
    retryTurn,
    startTurn,
    subscribeToCacheWrites,
  } = useMaiChatSession();
  const { phase, errorKind, turnKey } = getTurnState(patientId);

  useSyncExternalStore(subscribeToCacheWrites, getCacheWriteVersion);

  useEffect(() => {
    beginPatientSession(patientId);
  }, [beginPatientSession, patientId]);

  const historyArgs = useMemo(() => maiHistoryArgs(patientId), [patientId]);

  const {
    isFetching: isHistoryLoading,
    isError: isHistoryError,
    refetch: refetchHistory,
  } = useGetMaiMessagesQuery(historyArgs);

  const messages = useAppSelector(
    (state) =>
      maiApi.endpoints.getMaiMessages.select(historyArgs)(state).data ??
      EMPTY_MESSAGES,
  );

  const [pendingRatingIds, setPendingRatingIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [reasonPromptId, setReasonPromptId] = useState<string | null>(null);
  const { createToast } = useToastMessage();
  const [submitMaiFeedback] = useSubmitMaiFeedbackMutation();
  const [retractMaiFeedback] = useRetractMaiFeedbackMutation();

  const conversationIdRef = useRef<string>(uuidv4());
  const suggestedPrompts = useMaiSuggestedPrompts(patientId, conversationIdRef);
  const viewTrackedRef = useRef(false);
  // Read through a ref by the unmount reporter only: the context value is a new
  // object on most provider renders, so depending on it there would tear the
  // effect down and report a session end while the panel is still open.
  const analyticsRef = useRef(analytics);
  useLayoutEffect(() => {
    analyticsRef.current = analytics;
  });
  const historyFailureTrackedRef = useRef(false);
  // A thumbs-down reports before anything persists, since the reason modal comes
  // first — so an abandoned modal would re-report the same rating on reopen.
  const negativeRatedIdsRef = useRef<Set<string>>(new Set());
  // Bumped only on a clinician send, so Turn Number holds steady across retries.
  const sentSeqRef = useRef(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clinicianName =
    getPreferredFullName(
      currentUser?.chosenName,
      currentUser?.firstName,
      currentUser?.lastName,
      false,
    ) || 'You';

  const patchPatientMessages = useCallback(
    (recipe: (prevList: MaiMessage[]) => MaiMessage[]) =>
      patchMessages(patientId, recipe),
    [patchMessages, patientId],
  );

  const handleSend = useCallback(
    (text: string, meta: MaiSendMeta) => {
      sentSeqRef.current += 1;
      analytics?.trackEvent(MaiChatEvents.messageSent, {
        patientId,
        'Conversation Id': conversationIdRef.current,
        Trigger: meta.trigger,
        'Turn Number': sentSeqRef.current,
        'Character Count': text.length,
        ...(meta.promptId ? { 'Prompt Id': meta.promptId } : {}),
      });
      const userMessage: MaiMessage = {
        id: `user-${uuidv4()}`,
        role: 'user',
        text,
        status: 'sent',
        createdAt: new Date().toISOString(),
      };
      patchPatientMessages((prevList) => [...prevList, userMessage]);
      startTurn(patientId, text, conversationIdRef.current);
    },
    [analytics, patchPatientMessages, patientId, startTurn],
  );

  const handleRetry = useCallback(
    (kind: MaiChatErrorKind) => {
      analytics?.trackEvent(MaiChatEvents.turnRetried, {
        patientId,
        'Conversation Id': conversationIdRef.current,
        'Error Kind': MAI_CHAT_ERROR_KIND_LABELS[kind],
      });
      retryTurn(patientId);
    },
    [analytics, patientId, retryTurn],
  );

  const handleRegenerate = useCallback(
    (aiMessageId: string) => {
      const aiIndex = messages.findIndex(
        (message) => message.id === aiMessageId,
      );
      if (aiIndex === -1) return;
      const prompt = messages
        .slice(0, aiIndex)
        .findLast((message) => message.role === 'user');
      if (!prompt?.text) return;
      // Absent on a turn the server never acknowledged (an errored response can
      // still be regenerated), so there is no id to join back to.
      const serverMessageId = messages[aiIndex].messageId;
      analytics?.trackEvent(MaiChatEvents.responseRegenerated, {
        patientId,
        'Conversation Id': conversationIdRef.current,
        ...(serverMessageId ? { 'Message Id': serverMessageId } : {}),
      });
      patchPatientMessages((prevList) =>
        prevList.filter((message) => message.id !== aiMessageId),
      );
      startTurn(patientId, prompt.text, conversationIdRef.current);
    },
    [analytics, messages, patchPatientMessages, patientId, startTurn],
  );

  const handleToolStepsToggle = useCallback(
    (isExpanded: boolean, toolCount: number, serverMessageId?: string) => {
      analytics?.trackEvent(
        isExpanded
          ? MaiChatEvents.toolStepsExpanded
          : MaiChatEvents.toolStepsCollapsed,
        {
          patientId,
          'Conversation Id': conversationIdRef.current,
          'Tool Count': toolCount,
          // Absent while the response is still streaming — the id arrives with the
          // done frame, and the steps are expandable before then.
          ...(serverMessageId ? { 'Message Id': serverMessageId } : {}),
        },
      );
    },
    [analytics, patientId],
  );

  const applyRatingChange = useCallback(
    async (
      serverMessageId: string,
      rating: MaiFeedbackRating | undefined,
      request: () => Promise<unknown>,
      success: { id: string; text: string },
    ) => {
      setPendingRatingIds((prev) => new Set(prev).add(serverMessageId));
      try {
        await request();
        if (!mountedRef.current) return true;
        patchPatientMessages((prevList) =>
          prevList.map((message) =>
            message.messageId === serverMessageId
              ? { ...message, rating }
              : message,
          ),
        );
        createToast({
          icon: FAIconName.checkRegular,
          id: success.id,
          position: 'top',
          text: success.text,
          variant: 'success',
        });
        return true;
      } catch {
        if (!mountedRef.current) return false;
        createToast({
          icon: FAIconName.XmarkRegular,
          id: 'mai-chat-feedback-failure',
          position: 'top',
          text: MAI_FEEDBACK_ERROR_TOAST,
          variant: 'critical',
        });
        return false;
      } finally {
        if (mountedRef.current) {
          setPendingRatingIds((prev) => {
            const next = new Set(prev);
            next.delete(serverMessageId);
            return next;
          });
        }
      }
    },
    [createToast, patchPatientMessages],
  );

  const sendFeedback = useCallback(
    (
      serverMessageId: string,
      rating: MaiFeedbackRating,
      reason?: MaiFeedbackReason,
      comment?: string,
    ) =>
      applyRatingChange(
        serverMessageId,
        rating,
        () =>
          submitMaiFeedback({
            message_id: serverMessageId,
            rating,
            reason,
            comment,
          }).unwrap(),
        {
          id: 'mai-chat-feedback-success',
          text: MAI_FEEDBACK_SUCCESS_TOAST,
        },
      ),
    [applyRatingChange, submitMaiFeedback],
  );

  const retractFeedback = useCallback(
    (serverMessageId: string) =>
      applyRatingChange(
        serverMessageId,
        undefined,
        () => retractMaiFeedback(serverMessageId).unwrap(),
        {
          id: 'mai-chat-feedback-retracted',
          text: MAI_FEEDBACK_RETRACTED_TOAST,
        },
      ),
    [applyRatingChange, retractMaiFeedback],
  );

  const handleRate = useCallback(
    (serverMessageId: string, rating: MaiFeedbackRating) => {
      const current = messages.find(
        (message) => message.messageId === serverMessageId,
      )?.rating;
      if (current === rating) {
        // A later thumbs-down is a fresh rating, not the abandoned one.
        negativeRatedIdsRef.current.delete(serverMessageId);
        // Reported so a rating count reconciles with the feedback actually stored;
        // the retraction changes server state, so a silent one overstates ratings.
        analytics?.trackEvent(MaiChatEvents.responseRatingRetracted, {
          patientId,
          'Conversation Id': conversationIdRef.current,
          'Message Id': serverMessageId,
          Rating: RATING_LABELS[rating],
        });
        void retractFeedback(serverMessageId);
        return;
      }
      if (rating === 'down') {
        if (!negativeRatedIdsRef.current.has(serverMessageId)) {
          negativeRatedIdsRef.current.add(serverMessageId);
          analytics?.trackEvent(MaiChatEvents.responseRatedNegatively, {
            patientId,
            'Conversation Id': conversationIdRef.current,
            'Message Id': serverMessageId,
          });
        }
        setReasonPromptId(serverMessageId);
        return;
      }
      analytics?.trackEvent(MaiChatEvents.responseRatedPositively, {
        patientId,
        'Conversation Id': conversationIdRef.current,
        'Message Id': serverMessageId,
      });
      void sendFeedback(serverMessageId, 'up');
    },
    [analytics, messages, patientId, retractFeedback, sendFeedback],
  );

  const handleReasonSubmit = useCallback(
    (reason: MaiFeedbackReason, comment?: string) => {
      const serverMessageId = reasonPromptId;
      if (!serverMessageId) return;
      analytics?.trackEvent(MaiChatEvents.negativeFeedbackSubmitted, {
        patientId,
        'Conversation Id': conversationIdRef.current,
        'Message Id': serverMessageId,
        Reason: reason,
        'Has Comment': Boolean(comment && comment.trim().length > 0),
      });
      void sendFeedback(serverMessageId, 'down', reason, comment).then((ok) => {
        if (ok && mountedRef.current) setReasonPromptId(null);
      });
    },
    [analytics, patientId, reasonPromptId, sendFeedback],
  );

  // Cancel, dismiss and Esc all land here; a successful submit closes the modal
  // by clearing the prompt id itself, so it never reports as abandoned.
  const handleReasonClose = useCallback(() => {
    const serverMessageId = reasonPromptId;
    setReasonPromptId(null);
    if (!serverMessageId) return;
    analytics?.trackEvent(MaiChatEvents.negativeFeedbackCancelled, {
      patientId,
      'Conversation Id': conversationIdRef.current,
      'Message Id': serverMessageId,
    });
  }, [analytics, patientId, reasonPromptId]);

  // Reports the session that just ended when the clinician leaves the panel.
  useEffect(() => {
    // Captured (not read in cleanup) to satisfy exhaustive-deps.
    const conversationId = conversationIdRef.current;
    const openedAt = Date.now();
    return () => {
      // Only for a session that reported a view, so a bounce rate derived from
      // Message Count divides by the same set of panel opens Viewed counts.
      if (!viewTrackedRef.current) return;
      analyticsRef.current?.trackEvent(MaiChatEvents.sessionEnded, {
        patientId,
        'Conversation Id': conversationId,
        'Active Duration Ms': Date.now() - openedAt,
        // Clinician sends, not the rendered message count, so Mixpanel can read
        // both "did they engage" and "how deep" off this one number.
        'Message Count': sentSeqRef.current,
      });
    };
  }, [patientId]);

  // Re-armed when a load succeeds: a retry that fails again is a fresh failure.
  useEffect(() => {
    if (!isHistoryError) {
      historyFailureTrackedRef.current = false;
      return;
    }
    if (historyFailureTrackedRef.current) return;
    historyFailureTrackedRef.current = true;
    analytics?.trackEvent(MaiChatEvents.historyLoadFailed, {
      patientId,
      'Conversation Id': conversationIdRef.current,
    });
  }, [analytics, isHistoryError, patientId]);

  // One view per mount, once history settles — an errored load leaves messages
  // empty and would report a phantom 'History Message Count': 0.
  useEffect(() => {
    if (viewTrackedRef.current || isHistoryLoading || isHistoryError) return;
    viewTrackedRef.current = true;
    analytics?.trackEvent(MaiChatEvents.chatViewed, {
      patientId,
      'Conversation Id': conversationIdRef.current,
      'History Message Count': messages.length,
    });
  }, [analytics, isHistoryError, isHistoryLoading, messages.length, patientId]);

  const isComposerDisabled = phase === 'submitting' || phase === 'streaming';
  const isAwaitingResponse = phase === 'submitting';

  return (
    <ChakraFlex
      data-testid="mai-chat-panel"
      direction="column"
      height="100%"
      background="neutral.background.primary"
      borderRight="1px solid"
      borderColor="neutral.line"
    >
      <MaiChatHeader
        patientFirstName={patientFirstName}
        patientLastName={patientLastName}
      />
      <MaiChatStream
        messages={messages}
        isAwaitingResponse={isAwaitingResponse}
        errorKind={phase === 'error' ? errorKind : null}
        isHistoryLoading={isHistoryLoading}
        isHistoryError={isHistoryError}
        turnKey={turnKey}
        patientFirstName={patientFirstName}
        clinicianName={clinicianName}
        pendingRatingIds={pendingRatingIds}
        onRate={handleRate}
        onRegenerate={handleRegenerate}
        onRetry={handleRetry}
        onRetryHistory={refetchHistory}
        onToolStepsToggle={handleToolStepsToggle}
      />
      <MaiChatComposer
        onSend={handleSend}
        patientFirstName={patientFirstName}
        patientId={patientId}
        suggestedPrompts={suggestedPrompts}
        isDisabled={isComposerDisabled}
      />
      <MaiChatFeedbackModal
        isOpen={reasonPromptId !== null}
        isSubmitting={
          reasonPromptId !== null && pendingRatingIds.has(reasonPromptId)
        }
        onClose={handleReasonClose}
        onSubmit={handleReasonSubmit}
      />
    </ChakraFlex>
  );
};

export default MaiChatPanel;
